import * as THREE from 'three';
import Walls from '../Walls';
import MaterialUtils from '../../helpers/MaterialUtils';

export default class LStair {
    constructor(scene, model, mainSceneSetup) {
        this.scene = scene;
        this.model = model;
        this.mainSceneSetup = mainSceneSetup;
        this.treadGroup = new THREE.Group();
        this.scene.add(this.treadGroup);

        // Initialize walls
        this.walls = new Walls(this.scene, this.createWallMaterial());
        console.log('New LStair instance created with walls:', this.walls);

        // Default values for bottom and top flight treads
        this.bottomFlightTreads = 8;
        this.topFlightTreads = 8;
        this.treadWidth = 900;  // Default tread width in mm

        // Initialize numTreads right in the constructor
        this.numTreads = this.bottomFlightTreads + this.topFlightTreads;
        console.log('LStair numTreads initialized to:', this.numTreads);
        
        this.bottomFlightGroup = null;
        this.topFlightGroup = null;
        this.boundingBox = null;

        // Update MainSceneSetup with initial values
        this.mainSceneSetup.setFlightTreads(this.bottomFlightTreads, this.topFlightTreads);
         
        this.initialize();

        // Attach event listeners for cantilever radio buttons
        document.querySelectorAll('input[name="cantilever-option"]').forEach((radio) => {
            radio.addEventListener('change', () => {
                const isCantilever = document.querySelector('input[name="cantilever-option"]:checked').value === 'cantilever';
                const boundingBox = new THREE.Box3().setFromObject(this.treadGroup);
                const boxSize = boundingBox.getSize(new THREE.Vector3());
                const topFlightBoundingBox = new THREE.Box3().setFromObject(this.topFlightGroup);
                const topFlightBoxSize = topFlightBoundingBox.getSize(new THREE.Vector3());
                const bottomFlightBoundingBox = new THREE.Box3().setFromObject(this.bottomFlightGroup);
                const bottomFlightBoxSize = bottomFlightBoundingBox.getSize(new THREE.Vector3());

                this.updateWallsWithBoundingBox(
                    boundingBox, 
                    boxSize, 
                    topFlightBoundingBox, 
                    topFlightBoxSize,
                    bottomFlightBoundingBox, 
                    bottomFlightBoxSize
                );
            });
        });
    }

    async initialize() {
        this.createStaircase(this.bottomFlightTreads, this.topFlightTreads);
    
        const boundingBox = this.calculateBoundingBox();
        if (!boundingBox.isEmpty()) {
            this.isInitialized = true;
        } else {
            console.error('Bounding box not properly initialized');
        }
        return Promise.resolve();
    }

    createStaircase(bottomTreads, topTreads) {
        console.log(`Creating L-shaped staircase with ${bottomTreads} bottom treads and ${topTreads} top treads`);
        
        // Input validation
        if (isNaN(bottomTreads) || bottomTreads <= 0) {
            console.warn('Invalid bottomTreads value. Defaulting to 8.');
            bottomTreads = 8;
        }
        if (isNaN(topTreads) || topTreads <= 0) {
            console.warn('Invalid topTreads value. Defaulting to 8.');
            topTreads = 8;
        }

        this.bottomFlightTreads = bottomTreads;
        this.topFlightTreads = topTreads;
        this.numTreads = bottomTreads + topTreads;
        
        this.clearTreads();
        this.mainSceneSetup.setFlightTreads(bottomTreads, topTreads);

        const distance = 1.0;  // Distance between each tread
        const widthScale = this.treadWidth / 900;  // Calculate scale factor based on width

        let topTreadYOffset = 0;
        let topTreadZOffset = 0;

        // --- Bottom Flight ---
        const bottomFlightGroup = new THREE.Group();
        for (let i = 0; i < bottomTreads; i++) {
            const newTread = this.model.clone();
            newTread.scale.set(widthScale, 1, 1);
            const angle = THREE.MathUtils.degToRad(125);
            const yOffset = distance * i * Math.sin(angle) * 0.5;
            const zOffset = -distance * i * Math.cos(angle);

            newTread.position.set(0, yOffset, zOffset);
            newTread.castShadow = true;
            newTread.receiveShadow = true;
            bottomFlightGroup.add(newTread);

            if (i === bottomTreads - 1) {
                topTreadYOffset = yOffset;
                topTreadZOffset = zOffset;
            }
        }
        this.treadGroup.add(bottomFlightGroup);

        // Calculate bottom flight bounding box
        const bottomFlightBoundingBox = new THREE.Box3().setFromObject(bottomFlightGroup);
        const bottomFlightBoxSize = bottomFlightBoundingBox.getSize(new THREE.Vector3());

        // --- Landing ---
        const landingWidth = Math.abs(bottomFlightBoxSize.x);
        const landingDepth = landingWidth; // Make landing square based on scaled width
        const bottomFlightTopTreadY = Math.abs(bottomFlightBoxSize.y);

        const landing = new THREE.Mesh(
            new THREE.BoxGeometry(landingWidth, 0.2, landingDepth),
            MaterialUtils.createWoodMaterial()
        );

        landing.position.set(
            0,
            bottomFlightTopTreadY - 0.2,
            bottomFlightBoundingBox.max.z + (landingDepth / 2) - 0.3
        );
        
        landing.castShadow = true;
        landing.receiveShadow = true;
        this.treadGroup.add(landing);

        // --- Top Flight ---
        const topFlightGroup = new THREE.Group();
        const halfLandingWidth = landingWidth / 2;

        for (let i = 0; i < topTreads; i++) {
            const newTread = this.model.clone();
            newTread.scale.set(widthScale, 1, 1);
            const angle = THREE.MathUtils.degToRad(125);
            const yOffset = distance * (i + 1) * Math.sin(angle) * 0.5;
            const xOffset = -distance * (i + 1) * Math.cos(angle);

            newTread.position.set(
                xOffset + halfLandingWidth - 0.1,
                topTreadYOffset + yOffset + 0.3,
                landing.position.z
            );
            newTread.rotation.y = Math.PI / 2;
            newTread.castShadow = true;
            newTread.receiveShadow = true;
            topFlightGroup.add(newTread);

            if (i === topTreads - 1) {
                topTreadYOffset += yOffset;
                topTreadZOffset += halfLandingWidth;
            }
        }
        this.treadGroup.add(topFlightGroup);

        // Store references
        this.bottomFlightGroup = bottomFlightGroup;
        this.topFlightGroup = topFlightGroup;

        // Calculate final bounding boxes for wall positioning
        const boundingBox = new THREE.Box3().setFromObject(this.treadGroup);
        const boxSize = boundingBox.getSize(new THREE.Vector3());
        const topFlightBoundingBox = new THREE.Box3().setFromObject(topFlightGroup);
        const topFlightBoxSize = topFlightBoundingBox.getSize(new THREE.Vector3());

        // Update walls with all necessary bounding boxes
        if (boxSize && boxSize.x && boxSize.y && boxSize.z) {
            this.updateWallsWithBoundingBox(
                boundingBox,
                boxSize,
                topFlightBoundingBox,
                topFlightBoxSize,
                bottomFlightBoundingBox,
                bottomFlightBoxSize
            );
        }
    }

    calculateBoundingBox() {
        if (this.treadGroup.children.length === 0) {
            console.error('treadGroup is empty. Cannot calculate bounding box.');
            return new THREE.Box3();
        }

        const boundingBox = new THREE.Box3().setFromObject(this.treadGroup);
        const bottomFlightBoundingBox = new THREE.Box3().setFromObject(this.bottomFlightGroup);
        const topFlightBoundingBox = new THREE.Box3().setFromObject(this.topFlightGroup);

        this.boundingBox = boundingBox;
        this.bottomFlightBoundingBox = bottomFlightBoundingBox;
        this.topFlightBoundingBox = topFlightBoundingBox;

        return boundingBox;
    }

    updateWallsWithBoundingBox(boundingBox, boxSize, topFlightBoundingBox, topFlightBoxSize, bottomFlightBoundingBox, bottomFlightBoxSize) {
        if (!boxSize || !isFinite(boxSize.x) || !isFinite(boxSize.y) || !isFinite(boxSize.z)) {
            console.error('Invalid or undefined box size in updateWallsWithBoundingBox:', boxSize);
            return;
        }

        const wallHeight = 1.2;
        const wallThickness = 3;
        const isCantilever = document.querySelector('input[name="cantilever-option"]:checked').value === 'cantilever';
        const backWallHeight = isCantilever ? boxSize.y : wallHeight;
        const additionalWallCantileverX = isCantilever ? (wallThickness / 2) - 0.5 : wallThickness / 2;
        const backWallWidth = boxSize.x + (wallThickness * 2) - 0.3;
        const wallOffset = isCantilever ? 0 : 0.2;

        // Update back wall
        this.walls.backWall.geometry.dispose();
        this.walls.backWall.geometry = new THREE.BoxGeometry(backWallWidth, backWallHeight, wallThickness + wallOffset);
        this.walls.backWall.position.set(
            boundingBox.min.x + boxSize.x / 2 + wallOffset,
            boundingBox.max.y - backWallHeight / 2,
            topFlightBoundingBox.max.z + wallThickness / 2 + wallOffset
        );

        // Update side wall
        this.walls.sideWall.geometry.dispose();
        this.walls.sideWall.geometry = new THREE.BoxGeometry(wallThickness, boxSize.y, boxSize.z + wallOffset);
        this.walls.sideWall.position.set(
            boundingBox.min.x - wallThickness / 2 - wallOffset,
            boundingBox.min.y + boxSize.y / 2,
            boundingBox.min.z + boxSize.z / 2
        );

        // Update additional wall
        this.walls.additionalWall.geometry.dispose();
        this.walls.additionalWall.geometry = new THREE.BoxGeometry(wallThickness, wallHeight, wallThickness + 4);
        this.walls.additionalWall.position.set(
            boundingBox.max.x + additionalWallCantileverX - 0.3,
            boundingBox.max.y - wallHeight / 2,
            boundingBox.max.z - 1
        );
        this.walls.additionalWall.visible = true;

        this.scene.needsUpdate = true;
    }

    updateStairTreads(bottomTreads, topTreads) {
        this.bottomFlightTreads = bottomTreads;
        this.topFlightTreads = topTreads;
        this.numTreads = bottomTreads + topTreads;
        console.log('LStair Updated numTreads:', this.numTreads);
        this.mainSceneSetup.setFlightTreads(bottomTreads, topTreads);
        this.createStaircase(bottomTreads, topTreads);
    }

    updateTreadWidth(width) {
        if (width === this.treadWidth) return;
        this.treadWidth = width;
        this.createStaircase(this.bottomFlightTreads, this.topFlightTreads);
    }

    updateMaterial(selectedMaterial) {
        this.treadGroup.children.forEach(tread => {
            if (tread.geometry instanceof THREE.BoxGeometry) {
                // This is the landing
                MaterialUtils.updateTreadMaterial(selectedMaterial, tread);
            } else {
                // These are the treads
                const treadElement = tread.getObjectByName('Tread');
                if (treadElement) {
                    MaterialUtils.updateTreadMaterial(selectedMaterial, treadElement);
                }
            }
        });
    }

    clearTreads() {
        console.log("clearTreads called on", this);
        while (this.treadGroup.children.length > 0) {
            const tread = this.treadGroup.children.pop();
            this.scene.remove(tread);
            console.log("Removed tread:", tread);
            if (tread.geometry) {
                tread.geometry.dispose();
            }
            if (tread.material) {
                if (Array.isArray(tread.material)) {
                    tread.material.forEach(mat => mat.dispose());
                } else {
                    tread.material.dispose();
                }
            }
        }
    }

    createWallMaterial() {
        const textureLoader = new THREE.TextureLoader();
        const wallTexture = textureLoader.load('/textures/cross-laminated-timber-clt-fir-2000-mm-architextures.jpg');
        wallTexture.wrapS = THREE.RepeatWrapping;
        wallTexture.wrapT = THREE.RepeatWrapping;
        wallTexture.repeat.set(4, 4);
        return new THREE.MeshStandardMaterial({
            map: wallTexture,
            side: THREE.DoubleSide,
        });
    }
}
